"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BenefitCard from "@/components/BenefitCard";
import BottomCta from "@/components/BottomCta";
import OffshoreHero from "@/components/offshore/OffshoreHero";
import WhychooseOffshore from "@/components/offshore/WhychooseOffshore";

const BENEFITS = [
  { title: "International Operations", description: "Offshore companies are ideal for businesses operating globally with no requirement for UAE-based operations." },
  { title: "Asset Protection", description: "Protect your assets and intellectual property under a robust UAE legal framework." },
  { title: "100% Foreign Ownership", description: "Full ownership with complete control over your company structure and shareholding." },
  { title: "0% Corporate & Income Tax", description: "Pay no corporate or personal income taxes on your offshore company profits." },
  { title: "No Physical Office Required", description: "Offshore companies do not require a physical UAE office space or local presence." },
  { title: "Confidentiality", description: "UAE offshore structures offer strong privacy protections for shareholders and directors." },
];

export default function OffshorePage() {
  const [open, setOpen] = useState(null);

  const FAQS = [
    {
      question: "What is an offshore company in the UAE?",
      answer:
        "An offshore company in the UAE is a legal entity registered under a UAE offshore jurisdiction to support international business, asset protection, and tax-efficient holding structures.",
    },
    {
      question: "Do I need a physical office for an offshore company?",
      answer:
        "No. Offshore companies do not require a physical office or local operational presence in the UAE, which makes them ideal for international businesses and holding activities.",
    },
    {
      question: "Can offshore companies own property or open bank accounts?",
      answer:
        "Yes. Offshore companies can hold international assets, open corporate bank accounts, and own intellectual property, although property ownership in the UAE may be subject to local regulations.",
    },
    {
      question: "Are there taxes on offshore companies in the UAE?",
      answer:
        "Offshore companies generally benefit from zero corporate and personal income tax, making them attractive for global holding and trading structures.",
    },
    {
      question: "Who can be a shareholder or director of a UAE offshore company?",
      answer:
        "Foreign nationals can be full shareholders and directors of UAE offshore companies, offering confidentiality and flexibility for international ownership.",
    },
  ];

  return (
    <main className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <OffshoreHero />
      <WhychooseOffshore />

      <section className="py-14 sm:py-20 lg:py-14 px-4 sm:px-6 lg:px-10 bg-[#FAFAFA]">
        <div className="max-w-full mx-auto">
          <div className="text-left md:text-center mb-12">
            <p className="text-xs sm:text-sm font-bold text-[#B8893A] uppercase tracking-widest mb-3">
              Frequently Asked Questions
            </p>
            <h2
              className="font-bold text-[#000000]"
              style={{
                fontSize: "clamp(28px, 5vw, 48px)",
                lineHeight: "1.3",
              }}
            >
              Offshore Company Questions Answered
            </h2>
          </div>

          <div className="space-y-4">
            {FAQS.map((faq, index) => (
              <div key={index} className="border border-[#e7e7e7] rounded-2xl overflow-hidden bg-white">
                <button
                  onClick={() => setOpen(open === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-semibold text-black text-base md:text-lg">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`transition-transform duration-300 ${open === index ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    open === index ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-5 pb-5 text-gray-500 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
        title="Set Up Your Offshore Company"
        subtitle="International operations, full privacy, and zero tax — with complete expert support."
      />
      <Footer />
    </main>
  );
}
